import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { httpAddNeighborhoodsToSubgeo } from '@/features/subgeo/http/add-neighborhoods-to-subgeo.http.ts'
import { useSubgeoStore } from '@/features/subgeo/store/subgeo-store.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

interface UseQuickAssignNeighborhoodPayload {
	neighborhoodId: string
	targetSubgeoId: string
}

export const useQuickAssignNeighborhood = () => {
	const queryClient = useQueryClient()
	const { t } = useTranslation()

	return useMutation({
		mutationFn: ({
			neighborhoodId,
			targetSubgeoId
		}: UseQuickAssignNeighborhoodPayload) => {
			return httpAddNeighborhoodsToSubgeo({
				urlParams: { id: targetSubgeoId },
				payload: { neighborhoodIds: [neighborhoodId] },
				params: { responseType: 'extended' }
			})
		},
		onMutate: ({ neighborhoodId, targetSubgeoId }) => {
			const currentSubgeos = useSubgeoStore.getState().subgeos

			const updatedSubgeos = currentSubgeos.map((subgeo) => {
				if (subgeo.id === targetSubgeoId) {
					return {
						...subgeo,
						neighborhoodIds: subgeo.neighborhoodIds.includes(
							neighborhoodId
						)
							? subgeo.neighborhoodIds
							: [...subgeo.neighborhoodIds, neighborhoodId]
					}
				}
				return {
					...subgeo,
					neighborhoodIds: subgeo.neighborhoodIds.filter(
						(id) => id !== neighborhoodId
					)
				}
			})

			useSubgeoStore.getState().setSubgeos(updatedSubgeos)

			return { previousSubgeos: currentSubgeos }
		},
		onError: (error, _variables, context) => {
			if (context?.previousSubgeos) {
				useSubgeoStore.getState().setSubgeos(context.previousSubgeos)
			}
			errorHandler(error)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_SUBGEOS]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_NEIGHBORHOODS]
			})
			toast.success(t('subgeo:quick_assign_success'))
		}
	})
}
